import { ApiClient, RequestOptions } from "./apiClient";
import { EnvConfig } from "../config";

interface TbdbTokenEvent {
    success: boolean;
    errorMessage?: string;
}
type unsubscribeFn = () => void;

const publicApiClients: Record<string, PublicApiClient> = {};

export const getPublicApiClient = (config: EnvConfig): PublicApiClient => {
    const { publicApiBaseUrl, authIssuer, authScope, authClientId } = config;
    const key = `${publicApiBaseUrl}-${authIssuer}-${authScope}-${authClientId}`;
    if (!publicApiClients[key]) {
        publicApiClients[key] = new PublicApiClient(config);
    }
    return publicApiClients[key];
};

export class PublicApiClient extends ApiClient {
    private tbdbToken: string | null = null;
    private tbdbSubscribers: ((event: TbdbTokenEvent) => void)[] = [];

    subscribeToTbdbToken(onTbdbToken: (event: TbdbTokenEvent) => void): unsubscribeFn {
        this.tbdbSubscribers.push(onTbdbToken);
        if (this.tbdbToken) {
            onTbdbToken({ success: true });
        }
        return () => {
            this.tbdbSubscribers = this.tbdbSubscribers.filter((subscriber) => subscriber !== onTbdbToken);
        };
    }

    /**
     * Request function to send an authenticated POST request to the PublicAPI
     */
    async request<T>(path: string, options?: RequestOptions<T>) {
        if (this.config.useMockData) {
            return this.mockRequest<T>(options?.mockResponse as T);
        }

        if (this.authClientServiceCalls === null) {
            return Promise.reject(new Error("AuthClient not initialized"));
        }
        if (!this.tbdbToken) {
            return Promise.reject(new Error("No TBDB token"));
        }
        const { method = "POST", headers = {}, body, binary, responseType } = options || {};
        const tokenForBinary = binary ? { TbdbToken: this.tbdbToken } : {};
        const response = await fetch(`${this.config.publicApiBaseUrl}${path}`, {
            method,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                ...(tokenForBinary as Record<string, string>),
                ...headers
            },
            body: binary
                ? (body as BodyInit)
                : JSON.stringify({
                      ...(body as object),
                      token: this.tbdbToken
                  })
        });
        if (response.status === 200) {
            return responseType === "blob" ? response.blob() : response.json();
        }
        if (response.status === 401) {
            this.authClientServiceCalls.deleteAccessToken();
            throw new Error("Unauthorized");
        }
        throw new Error(`${(await response.text()) || response.status}`);
    }

    protected onAuth(): void {
        this.requestTbdbToken()
            .then((token) => {
                this.tbdbToken = token;
                this.tbdbSubscribers.forEach((subscriber) => {
                    subscriber({ success: true });
                });
            })
            .catch((error) => {
                const errorMessage = `Error while fetching tbdb token: ${error}`;
                // eslint-disable-next-line no-console
                console.error(errorMessage);
                this.tbdbSubscribers.forEach((subscriber) => {
                    subscriber({ success: false, errorMessage });
                });
            });
    }

    async requestTbdbToken(): Promise<string> {
        const { publicApiBaseUrl, publicAPIVersion, publicAPIClientType, useMockData } = this.config;
        if (useMockData) {
            return this.mockRequest<string>("mockToken");
        }
        return fetch(`${publicApiBaseUrl}/user/createticket`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.token}`
            },
            body: JSON.stringify({
                clientType: publicAPIClientType,
                apiVersion: publicAPIVersion
            })
        })
            .then((response) => {
                if (response.status === 401) {
                    return Promise.reject("Unauthorized");
                }
                return response.json();
            })
            .then((data) => data.token);
    }
}
